/**
 * VideoSubmitPage.tsx
 * ─────────────────────────────────────────────────────────────
 * 크리에이터 영상 등록 신청 페이지
 *
 * Phase 1: YouTube URL 등록 ✅
 * Phase 2: 직접 파일 업로드 ✅ (활성화)
 */

import React, { useState, useRef, useCallback } from 'react'
import {
  Youtube, Upload, CheckCircle, AlertCircle, Loader2,
  Award, Clock, Eye, Star, ChevronDown, ChevronUp,
  FileVideo, Link, User, BookOpen, Tag, Info,
  X, Play, Film, Coins, Users, TrendingUp,
} from 'lucide-react'
import {
  useVideoStore,
  CREDIT_PER_VIEW,
  getYouTubeThumbnail,
  extractYouTubeId,
} from '../store/videoStore'

// ── 개념 목록 (LearnPage STEPS와 동기화) ──────────────────────
const CONCEPT_OPTIONS = [
  { id: 'LS01', label: '변수 이해',              grade: '중2' },
  { id: 'LS02', label: '순서쌍과 좌표평면',       grade: '중2' },
  { id: 'LS03', label: '표에서 함수 관계 찾기',   grade: '중2' },
  { id: 'LS04', label: '변화율',                  grade: '중2' },
  { id: 'LS05', label: '기울기(slope) 이해',      grade: '중2' },
  { id: 'LS06', label: 'y절편 이해',              grade: '중2' },
  { id: 'LS07', label: '1차 함수식 y=ax+b',       grade: '중2' },
  { id: 'LS08', label: '정비례 vs 1차 함수',      grade: '중2' },
  { id: 'LS09', label: '그래프 그리기',           grade: '중2' },
  { id: 'LS10', label: '그래프에서 식 구하기',    grade: '중2' },
  { id: 'LS11', label: '실생활 문장제 → 함수식',  grade: '중2' },
  { id: 'LS12', label: '오답 분석과 개념 정리',   grade: '중2' },
]

// ── FAQ 데이터 ─────────────────────────────────────────────────
const FAQ = [
  {
    q: '어떤 영상을 올릴 수 있나요?',
    a: '중학교 수학 개념을 5분 이내로 설명하는 영상입니다. YouTube 링크 등록 또는 MP4 파일 직접 업로드를 지원합니다. 정확한 수학 내용, 명확한 설명, 저작권 문제 없는 음악·이미지 사용이 필요합니다.',
  },
  {
    q: '크레딧은 어떻게 계산되나요?',
    a: `유효 시청 1회당 ${CREDIT_PER_VIEW}원이 적립됩니다. 유효 시청은 30초 이상, 영상 길이의 50% 이상 시청한 경우입니다. 같은 학생이 같은 영상을 하루에 여러 번 봐도 1회만 인정됩니다.`,
  },
  {
    q: '검수는 얼마나 걸리나요?',
    a: 'AI가 1차로 내용 적합성(제목·설명·길이·링크 등)을 자동 검토한 후, 관리자가 최종 승인합니다. 보통 1~3 영업일 이내에 결과를 이메일로 알려드립니다.',
  },
  {
    q: '직접 업로드 시 파일 규격은?',
    a: 'MP4(H.264) 형식, 최대 500MB, 5분 이내 권장합니다. 해상도는 720p 이상이면 좋습니다. 파일은 서버에 안전하게 저장되며, 승인 후 학생들에게 노출됩니다.',
  },
  {
    q: '크레딧 정산은 어떻게 하나요?',
    a: '누적 크레딧이 10,000원 이상이면 정산 신청이 가능합니다. 크리에이터 대시보드에서 계좌 정보를 입력하고 정산 요청하면 영업일 기준 5일 이내 입금됩니다.',
  },
  {
    q: '영상이 거절되면 어떻게 되나요?',
    a: '거절 사유를 이메일로 상세히 안내해 드립니다. 수정 후 재신청이 가능하며, 수정 횟수 제한은 없습니다.',
  },
]

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <span className="text-sm font-semibold text-slate-700">{q}</span>
        {open ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0" />
               : <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3 bg-slate-50/50">
          {a}
        </div>
      )}
    </div>
  )
}

// ── 파일 업로드 드롭존 컴포넌트 ───────────────────────────────
interface UploadDropzoneProps {
  onFile: (file: File, durationSec: number) => void
  uploadFile: File | null
  uploadPreviewUrl: string
  onRemove: () => void
  durationSec: number
  onDurationChange: (v: number) => void
  error?: string
}

function UploadDropzone({
  onFile, uploadFile, uploadPreviewUrl, onRemove, durationSec, onDurationChange, error,
}: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('video/')) return
    // 영상 길이 자동 감지
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      const dur = Math.round(video.duration) || 300
      URL.revokeObjectURL(video.src)
      onFile(file, dur)
    }
    video.src = URL.createObjectURL(file)
  }, [onFile])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) processFile(file)
  }, [processFile])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
  }

  const fileSizeMB = uploadFile ? (uploadFile.size / 1024 / 1024).toFixed(1) : ''

  if (uploadFile) {
    return (
      <div className="space-y-3">
        {/* 파일 카드 */}
        <div className="border-2 border-emerald-300 bg-emerald-50 rounded-xl p-4 flex items-start gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Film size={20} className="text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-emerald-800 truncate">{uploadFile.name}</p>
            <p className="text-xs text-emerald-600 mt-0.5">
              {fileSizeMB} MB · {Math.floor(durationSec / 60)}분 {durationSec % 60}초
              {durationSec > 300 && <span className="text-red-500 ml-2">⚠ 5분 초과</span>}
            </p>
          </div>
          <button
            onClick={onRemove}
            className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-200 hover:bg-red-100 hover:text-red-600 flex items-center justify-center transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 미리보기 */}
        {uploadPreviewUrl && (
          <div className="rounded-xl overflow-hidden border border-slate-200 aspect-video bg-slate-900">
            <video
              src={uploadPreviewUrl}
              controls
              className="w-full h-full"
              preload="metadata"
            />
          </div>
        )}

        {/* 영상 길이 수동 수정 */}
        <div>
          <label className="text-xs font-bold text-slate-500 mb-1 block">영상 길이 (자동 감지 · 수정 가능)</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={durationSec}
              onChange={e => onDurationChange(Number(e.target.value))}
              min={10}
              max={600}
              className="w-28 border-2 border-slate-200 focus:border-violet-400 rounded-xl px-3 py-2 text-sm outline-none"
            />
            <span className="text-sm text-slate-500">
              = {Math.floor(durationSec / 60)}분 {durationSec % 60}초
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/mov,video/avi,video/webm,video/*"
        className="hidden"
        onChange={handleChange}
      />
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          dragging
            ? 'border-violet-400 bg-violet-50'
            : error
              ? 'border-red-300 bg-red-50'
              : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/30'
        }`}
      >
        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${
          dragging ? 'bg-violet-100' : 'bg-slate-100'
        }`}>
          <Upload size={24} className={dragging ? 'text-violet-500' : 'text-slate-400'} />
        </div>
        <p className="text-sm font-bold text-slate-600 mb-1">
          {dragging ? '여기에 놓으세요!' : '파일을 드래그하거나 클릭해서 선택'}
        </p>
        <p className="text-xs text-slate-400">
          MP4 · MOV · WebM · 최대 500MB · 5분 이내 권장
        </p>
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// 크리에이터 모집 히어로 배너 (페이지 상단)
// ─────────────────────────────────────────────────────────────
function CreatorHeroBanner() {
  const stats = [
    { icon: <Coins size={16} className="text-yellow-500" />,    value: `${CREDIT_PER_VIEW}원`,    label: '유효 시청 1회당' },
    { icon: <Clock size={16} className="text-blue-500" />,      value: '5분 이내',                label: '권장 영상 길이' },
    { icon: <Users size={16} className="text-emerald-500" />,   value: '공개 모집 중',            label: '크리에이터' },
    { icon: <TrendingUp size={16} className="text-violet-500" />, value: 'AI + 관리자',           label: '2단계 검수' },
  ]
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-blue-600 p-6 text-white mb-6">
      {/* 배경 장식 */}
      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
      <div className="absolute -left-4 -bottom-4 w-24 h-24 rounded-full bg-white/5" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">🎬</span>
          <span className="text-xs font-bold bg-white/20 px-2.5 py-0.5 rounded-full tracking-wide">
            크리에이터 공개 모집
          </span>
        </div>
        <h1 className="text-2xl font-black mb-1">수학 영상 크리에이터가 되어보세요!</h1>
        <p className="text-sm text-violet-200 mb-5 leading-relaxed">
          중학교 수학 개념을 5분 안에 쉽게 설명하는 영상을 올려주세요.<br />
          학생들이 시청할 때마다 자동으로 크레딧이 쌓입니다.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {stats.map(s => (
            <div key={s.label} className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center">
              <div className="flex justify-center mb-1">{s.icon}</div>
              <div className="text-base font-black">{s.value}</div>
              <div className="text-xs text-violet-200">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// 메인 페이지
// ─────────────────────────────────────────────────────────────
export default function VideoSubmitPage() {
  const { submitVideo, registerCreator } = useVideoStore(s => ({
    submitVideo:      s.submitVideo,
    registerCreator:  s.registerCreator,
  }))

  // ── 탭 ────────────────────────────────────────────────────
  const [tab, setTab] = useState<'submit' | 'faq'>('submit')

  // ── 업로드 방식 ───────────────────────────────────────────
  const [sourceType, setSourceType] = useState<'youtube' | 'upload'>('youtube')

  // ── 직접 업로드 파일 상태 ─────────────────────────────────
  const [uploadFile, setUploadFile]           = useState<File | null>(null)
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState('')

  // ── 폼 상태 ───────────────────────────────────────────────
  const [form, setForm] = useState({
    creatorName:  '',
    creatorEmail: '',
    creatorBio:   '',
    conceptId:    '',
    title:        '',
    description:  '',
    youtubeUrl:   '',
    durationSec:  300,
    tags:         '',
  })
  const [errors, setErrors]     = useState<Record<string, string>>({})
  const [step, setStep]         = useState<'form' | 'preview' | 'done'>('form')
  const [submitting, setSubmitting] = useState(false)

  // ── YouTube 미리보기 ──────────────────────────────────────
  const youtubeId       = extractYouTubeId(form.youtubeUrl)
  const thumbnailUrl    = youtubeId ? getYouTubeThumbnail(form.youtubeUrl) : ''
  const selectedConcept = CONCEPT_OPTIONS.find(c => c.id === form.conceptId)

  const set = (key: string, value: string | number) =>
    setForm(prev => ({ ...prev, [key]: value }))

  // ── 유효성 검사 ───────────────────────────────────────────
  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.creatorName.trim())  e.creatorName  = '이름을 입력해주세요'
    if (!form.creatorEmail.trim()) e.creatorEmail = '이메일을 입력해주세요'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.creatorEmail))
      e.creatorEmail = '올바른 이메일 형식이 아닙니다'
    if (!form.conceptId)           e.conceptId    = '개념을 선택해주세요'
    if (!form.title.trim())        e.title        = '영상 제목을 입력해주세요'
    if (!form.description.trim())  e.description  = '설명을 입력해주세요'
    if (sourceType === 'youtube') {
      if (!form.youtubeUrl.trim()) e.youtubeUrl = 'YouTube URL을 입력해주세요'
      else if (!youtubeId)         e.youtubeUrl = '올바른 YouTube URL이 아닙니다'
    }
    if (sourceType === 'upload') {
      if (!uploadFile)             e.uploadFile = '영상 파일을 선택해주세요'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handlePreview = () => {
    if (validate()) setStep('preview')
  }

  const handleSubmit = () => {
    setSubmitting(true)
    setTimeout(() => {
      const creatorId = registerCreator({
        name:  form.creatorName,
        email: form.creatorEmail,
        bio:   form.creatorBio,
      })

      const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean)
      if (selectedConcept && !tags.includes(selectedConcept.label)) {
        tags.push(selectedConcept.label)
      }

      // 직접 업로드: uploadPath는 Phase 2에서 실제 S3/R2 경로로 교체
      // 현재는 blob URL을 임시 uploadPath로 저장
      submitVideo({
        conceptId:    form.conceptId,
        conceptLabel: selectedConcept?.label ?? form.conceptId,
        title:        form.title,
        creatorId,
        creatorName:  form.creatorName,
        source:       sourceType,
        youtubeUrl:   sourceType === 'youtube' ? form.youtubeUrl : undefined,
        uploadPath:   sourceType === 'upload'  ? uploadPreviewUrl : undefined,
        thumbnailUrl: sourceType === 'upload'
          ? `https://placehold.co/320x180/6d28d9/ffffff?text=${encodeURIComponent(form.title)}`
          : (thumbnailUrl || `https://placehold.co/320x180/e2e8f0/94a3b8?text=${encodeURIComponent(form.title)}`),
        durationSec:  form.durationSec,
        description:  form.description,
        tags,
      })

      setSubmitting(false)
      setStep('done')
    }, 1200)
  }

  // ─────────────────────────────────────────────────────────
  // 완료 화면
  // ─────────────────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
            <CheckCircle size={36} className="text-white" />
          </div>
          <h2 className="text-2xl font-black text-slate-800 mb-2">신청 완료!</h2>
          <p className="text-slate-500 mb-6 leading-relaxed">
            영상 등록 신청이 접수되었습니다.<br />
            AI 1차 검토 후 관리자가 최종 승인하면 이메일({form.creatorEmail})로 알려드립니다.
          </p>
          <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-2xl p-5 text-left mb-6">
            <p className="text-sm font-bold text-violet-700 mb-3">📋 신청 내역</p>
            <div className="space-y-2 text-sm text-slate-600">
              <div className="flex justify-between">
                <span className="text-slate-400">개념</span>
                <span className="font-semibold">{selectedConcept?.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">제목</span>
                <span className="font-semibold truncate max-w-xs">{form.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">업로드 방식</span>
                <span className="font-semibold">{sourceType === 'youtube' ? 'YouTube 링크' : '직접 업로드 (MP4)'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">크레딧 단가</span>
                <span className="font-bold text-violet-700">유효 시청 1회당 {CREDIT_PER_VIEW}원</span>
              </div>
            </div>
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setStep('form')
                setForm({ creatorName: '', creatorEmail: '', creatorBio: '', conceptId: '', title: '', description: '', youtubeUrl: '', durationSec: 300, tags: '' })
                setUploadFile(null)
                setUploadPreviewUrl('')
              }}
              className="px-5 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors text-sm"
            >
              + 다른 영상 등록
            </button>
            <a
              href="/creator-dashboard"
              className="px-5 py-2.5 rounded-xl bg-violet-500 text-white font-bold text-sm hover:bg-violet-600 transition-colors"
            >
              내 크레딧 확인 →
            </a>
          </div>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────
  // 미리보기 화면
  // ─────────────────────────────────────────────────────────
  if (step === 'preview') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <h1 className="text-xl font-black text-slate-800 mb-1">신청 내용 확인</h1>
        <p className="text-sm text-slate-500 mb-6">아래 내용이 맞으면 "최종 제출"을 눌러주세요.</p>

        {/* 썸네일 / 영상 미리보기 */}
        {sourceType === 'youtube' && thumbnailUrl && (
          <div className="rounded-2xl overflow-hidden mb-4 aspect-video bg-slate-100">
            <img src={thumbnailUrl} alt="thumbnail" className="w-full h-full object-cover" />
          </div>
        )}
        {sourceType === 'upload' && uploadPreviewUrl && (
          <div className="rounded-2xl overflow-hidden mb-4 aspect-video bg-slate-900">
            <video src={uploadPreviewUrl} controls className="w-full h-full" preload="metadata" />
          </div>
        )}

        <div className="bg-white border-2 border-slate-200 rounded-2xl p-5 space-y-3 mb-6">
          {[
            ['개념', selectedConcept?.label],
            ['제목', form.title],
            ['크리에이터', `${form.creatorName} (${form.creatorEmail})`],
            ['업로드', sourceType === 'youtube'
              ? `YouTube: ${form.youtubeUrl}`
              : `직접 업로드: ${uploadFile?.name ?? ''}  (${(form.durationSec / 60).toFixed(1)}분)`],
            ['설명', form.description],
            ['태그', form.tags || '(없음)'],
          ].map(([label, val]) => (
            <div key={label} className="flex gap-3">
              <span className="text-xs text-slate-400 w-16 flex-shrink-0 pt-0.5">{label}</span>
              <span className="text-sm text-slate-700 flex-1 leading-relaxed break-all">{val}</span>
            </div>
          ))}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6 flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            제출 후 AI + 관리자 검수를 거칩니다. 승인 전까지는 학생에게 노출되지 않습니다.
            저작권 위반 콘텐츠는 즉시 삭제 및 계정 정지 처리됩니다.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setStep('form')}
            className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-colors"
          >
            ← 수정하기
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-violet-500 to-blue-500 text-white font-black hover:opacity-90 transition-all shadow-md disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting
              ? <><Loader2 size={16} className="animate-spin" /> 제출 중...</>
              : <><CheckCircle size={16} /> 최종 제출</>}
          </button>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────
  // 메인 폼
  // ─────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* ── 크리에이터 모집 히어로 배너 ───────────────────── */}
      <CreatorHeroBanner />

      {/* 탭 */}
      <div className="flex gap-1 mb-6 bg-slate-100 rounded-xl p-1">
        {([['submit', '📋 영상 등록'], ['faq', '❓ FAQ']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${
              tab === id ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* FAQ 탭 */}
      {tab === 'faq' ? (
        <div className="space-y-3">
          {FAQ.map(item => <FaqItem key={item.q} {...item} />)}
        </div>
      ) : (
        /* 등록 폼 탭 */
        <div className="space-y-6">

          {/* ── 크리에이터 정보 ─────────────────────────────── */}
          <div className="bg-white border-2 border-slate-200 rounded-2xl p-5">
            <h2 className="text-sm font-black text-slate-700 flex items-center gap-2 mb-4">
              <User size={14} className="text-violet-500" /> 크리에이터 정보
            </h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">이름 / 채널명 *</label>
                <input
                  value={form.creatorName}
                  onChange={e => set('creatorName', e.target.value)}
                  placeholder="홍길동 / 수학왕TV"
                  className={`w-full border-2 rounded-xl px-3 py-2.5 text-sm outline-none transition-colors ${
                    errors.creatorName ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-violet-400'
                  }`}
                />
                {errors.creatorName && <p className="text-xs text-red-500 mt-1">{errors.creatorName}</p>}
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">이메일 * (정산 및 검수 결과 수신)</label>
                <input
                  value={form.creatorEmail}
                  onChange={e => set('creatorEmail', e.target.value)}
                  type="email"
                  placeholder="creator@example.com"
                  className={`w-full border-2 rounded-xl px-3 py-2.5 text-sm outline-none transition-colors ${
                    errors.creatorEmail ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-violet-400'
                  }`}
                />
                {errors.creatorEmail && <p className="text-xs text-red-500 mt-1">{errors.creatorEmail}</p>}
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">소개 (선택)</label>
                <textarea
                  value={form.creatorBio}
                  onChange={e => set('creatorBio', e.target.value)}
                  placeholder="간단한 자기소개를 입력해주세요"
                  rows={2}
                  className="w-full border-2 border-slate-200 focus:border-violet-400 rounded-xl px-3 py-2.5 text-sm outline-none resize-none transition-colors"
                />
              </div>
            </div>
          </div>

          {/* ── 영상 정보 ──────────────────────────────────── */}
          <div className="bg-white border-2 border-slate-200 rounded-2xl p-5">
            <h2 className="text-sm font-black text-slate-700 flex items-center gap-2 mb-4">
              <FileVideo size={14} className="text-blue-500" /> 영상 정보
            </h2>
            <div className="space-y-4">

              {/* 개념 선택 */}
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">
                  <BookOpen size={11} className="inline mr-1" /> 설명할 개념 *
                </label>
                <select
                  value={form.conceptId}
                  onChange={e => set('conceptId', e.target.value)}
                  className={`w-full border-2 rounded-xl px-3 py-2.5 text-sm outline-none transition-colors bg-white ${
                    errors.conceptId ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-blue-400'
                  }`}
                >
                  <option value="">개념을 선택하세요</option>
                  {CONCEPT_OPTIONS.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.id} · {c.label} ({c.grade})
                    </option>
                  ))}
                </select>
                {errors.conceptId && <p className="text-xs text-red-500 mt-1">{errors.conceptId}</p>}
              </div>

              {/* 제목 */}
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">영상 제목 *</label>
                <input
                  value={form.title}
                  onChange={e => set('title', e.target.value)}
                  placeholder="기울기란 무엇인가? — 5분 완성"
                  maxLength={60}
                  className={`w-full border-2 rounded-xl px-3 py-2.5 text-sm outline-none transition-colors ${
                    errors.title ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-blue-400'
                  }`}
                />
                <div className="flex justify-between">
                  {errors.title ? <p className="text-xs text-red-500 mt-1">{errors.title}</p> : <span />}
                  <p className="text-xs text-slate-300 mt-1">{form.title.length}/60</p>
                </div>
              </div>

              {/* 설명 */}
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">영상 소개 * (200자 이내)</label>
                <textarea
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="이 영상에서 배울 내용을 간단히 설명해주세요"
                  maxLength={200}
                  rows={3}
                  className={`w-full border-2 rounded-xl px-3 py-2.5 text-sm outline-none resize-none transition-colors ${
                    errors.description ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-blue-400'
                  }`}
                />
                <div className="flex justify-between">
                  {errors.description ? <p className="text-xs text-red-500 mt-1">{errors.description}</p> : <span />}
                  <p className="text-xs text-slate-300">{form.description.length}/200</p>
                </div>
              </div>

              {/* 태그 */}
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">
                  <Tag size={11} className="inline mr-1" /> 태그 (콤마로 구분)
                </label>
                <input
                  value={form.tags}
                  onChange={e => set('tags', e.target.value)}
                  placeholder="기울기, 변화율, 중2, 1차함수"
                  className="w-full border-2 border-slate-200 focus:border-blue-400 rounded-xl px-3 py-2.5 text-sm outline-none transition-colors"
                />
              </div>
            </div>
          </div>

          {/* ── 업로드 방식 ────────────────────────────────── */}
          <div className="bg-white border-2 border-slate-200 rounded-2xl p-5">
            <h2 className="text-sm font-black text-slate-700 flex items-center gap-2 mb-4">
              <Upload size={14} className="text-emerald-500" /> 영상 업로드
            </h2>

            {/* 방식 선택 */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {([
                { id: 'youtube', icon: <Youtube size={18} className="text-red-500" />, label: 'YouTube 링크', desc: '무료 · 권장' },
                { id: 'upload',  icon: <Upload   size={18} className="text-blue-500" />, label: '직접 업로드',  desc: 'MP4 · 최대 500MB' },
              ] as const).map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setSourceType(opt.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    sourceType === opt.id
                      ? 'border-violet-400 bg-violet-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {opt.icon}
                    <span className="text-sm font-bold text-slate-700">{opt.label}</span>
                  </div>
                  <span className="text-xs text-slate-400">{opt.desc}</span>
                </button>
              ))}
            </div>

            {/* YouTube URL 입력 */}
            {sourceType === 'youtube' && (
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 flex items-center gap-1 block">
                  <Link size={11} /> YouTube URL *
                </label>
                <input
                  value={form.youtubeUrl}
                  onChange={e => set('youtubeUrl', e.target.value)}
                  placeholder="https://youtu.be/xxxxx 또는 https://www.youtube.com/watch?v=xxxxx"
                  className={`w-full border-2 rounded-xl px-3 py-2.5 text-sm outline-none transition-colors ${
                    errors.youtubeUrl ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-violet-400'
                  }`}
                />
                {errors.youtubeUrl && <p className="text-xs text-red-500 mt-1">{errors.youtubeUrl}</p>}

                {/* 썸네일 미리보기 */}
                {youtubeId && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-slate-200">
                    <img
                      src={thumbnailUrl}
                      alt="preview"
                      className="w-full aspect-video object-cover"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                    <div className="p-2 bg-slate-50 flex items-center gap-2">
                      <CheckCircle size={12} className="text-emerald-500" />
                      <span className="text-xs text-emerald-700 font-semibold">YouTube 영상 확인됨</span>
                    </div>
                  </div>
                )}

                {/* 영상 길이 */}
                <div className="mt-3">
                  <label className="text-xs font-bold text-slate-500 mb-1 block">
                    영상 길이 (초) — 직접 입력
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={form.durationSec}
                      onChange={e => set('durationSec', Number(e.target.value))}
                      min={30}
                      max={600}
                      className="w-28 border-2 border-slate-200 focus:border-violet-400 rounded-xl px-3 py-2 text-sm outline-none"
                    />
                    <span className="text-sm text-slate-500">
                      = {Math.floor(form.durationSec / 60)}분 {form.durationSec % 60}초
                    </span>
                    {form.durationSec > 300 && (
                      <span className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle size={11} /> 5분 초과 — 검수에서 반려될 수 있음
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 직접 업로드 드롭존 */}
            {sourceType === 'upload' && (
              <UploadDropzone
                onFile={(file, durationSec) => {
                  setUploadFile(file)
                  setUploadPreviewUrl(URL.createObjectURL(file))
                  set('durationSec', durationSec)
                }}
                uploadFile={uploadFile}
                uploadPreviewUrl={uploadPreviewUrl}
                onRemove={() => { setUploadFile(null); setUploadPreviewUrl('') }}
                durationSec={form.durationSec}
                onDurationChange={v => set('durationSec', v)}
                error={errors.uploadFile}
              />
            )}
          </div>

          {/* 제출 안내 */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3">
            <Info size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700 leading-relaxed">
              <p className="font-bold mb-1">제출 전 확인사항</p>
              <ul className="space-y-0.5 list-disc list-inside">
                <li>영상 내용이 수학적으로 정확한가요?</li>
                <li>저작권 문제 없는 음악/이미지를 사용했나요?</li>
                <li>영상 길이가 5분 이내인가요?</li>
                <li>학생이 이해하기 쉽게 설명하고 있나요?</li>
              </ul>
            </div>
          </div>

          {/* 제출 버튼 */}
          <button
            onClick={handlePreview}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-violet-500 to-blue-500 text-white font-black text-base shadow-lg hover:opacity-90 transition-all flex items-center justify-center gap-2"
          >
            <Play size={18} /> 미리보기 확인 →
          </button>
        </div>
      )}
    </div>
  )
}
